import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { Icons } from '../../../../assets';
import { AccountContext, AccountContextInterface } from '../../../../contexts';
import { Cred } from '../../../../models';
import { UserSkill } from '../../../../models/skill.model';
import { Tab } from '../../../../types';
import { formatEnumValue } from '../../../../utils';
import { getConsistentColor } from '../../../../utils/profile-picture-utils';

type SkillCardProps = {
  skill: UserSkill;
  onSkillClick: (skill: UserSkill, type: Tab.Creds | Tab.Reccos) => void;
};

const SkillCard: FC<SkillCardProps> = ({ skill, onSkillClick }) => {
  const { accountService } = useContext<AccountContextInterface>(AccountContext);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [loadingMedia, setLoadingMedia] = useState<boolean>(false);
  const [creds, setCreds] = useState<Cred[]>([]);

  useEffect(() => {
    const fetchCreds = async () => {
      if (skill.credCount === 0) return;
      setLoadingMedia(true);
      try {
        const { data, error } = await accountService.getCreds(skill.accountId.id, [], 1, 3, [skill.id]);
        if (data && !error) {
          setCreds(data);
        }
      } catch (err) {
        /* empty */
      } finally {
        setLoadingMedia(false);
      }
    };
    fetchCreds();
  }, [accountService, skill.accountId.id, skill.credCount, skill.id]);

  const allResources = creds.flatMap(cred => cred.resources || []);

  return (
    <div className="bg-[#F5F5F7] p-4 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center" onClick={() => onSkillClick(skill, Tab.Creds)}>
          <div
            className="flex h-12 items-center justify-center rounded-full w-12"
            style={{ backgroundColor: getConsistentColor(skill.skill.name) }}>
            <span className="font-semibold text-black text-sm">{skill.skill.name.charAt(0)}</span>
          </div>
          <div>
            <div className="font-semibold text-base text-[#1A1A1A]">{skill.skill.name}</div>
            <div className="font-normal text-xs text-[#1A1A1A]">{formatEnumValue(skill.type)}</div>
          </div>
        </div>
        {skill.credCount > 0 && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => onSkillClick(skill, Tab.Creds)}>
              <img src={Icons.CRED_ICON} alt="cred" className="h-5 w-5" />
              <span className="font-semibold text-xs text-[#2B2B2B]">{skill.credCount}</span>
            </div>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => onSkillClick(skill, Tab.Reccos)}>
              <img src={Icons.RECCO_ICON_V2} alt="recco" className="h-5 w-5" />
              <span className="font-semibold text-xs text-[#2B2B2B]">{skill.reccoCount}</span>
            </div>
          </div>
        )}
      </div>
      {loadingMedia ? (
        <div className="bg-gray-200 h-96 items-center justify-center mt-4 rounded-lg w-full"></div>
      ) : (
        allResources.length > 0 && (
          <Carousel
            className="mt-4 w-full"
            emulateTouch
            showIndicators={false}
            showStatus={false}
            showThumbs={false}
            swipeable
            useKeyboardArrows
            onChange={index => {
              videoRefs.current.forEach((video, i) => {
                if (video) {
                  if (i === index) {
                    video.play().catch(() => {});
                  } else {
                    video.pause();
                  }
                }
              });
            }}>
            {allResources.map((item, index) => (
              <div
                key={index}
                className="bg-black overflow-hidden pb-[100%] relative rounded-lg w-full"
                onClick={() => onSkillClick(skill, Tab.Creds)}>
                {item.type === 'video' ? (
                  <video
                    className="absolute h-full left-0 object-contain top-0 w-full"
                    ref={el => (videoRefs.current[index] = el)}
                    src={item.url}
                    autoPlay
                    controls
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img src={item.url} alt="Media" className="absolute h-full left-0 object-contain top-0 w-full" />
                )}
              </div>
            ))}
          </Carousel>
        )
      )}
    </div>
  );
};

export default SkillCard;
